import { useEffect } from "react";

import { FormControlLabel, Switch } from "@mui/material";
import { LegacyCard, LegacyStack, Text, TextField } from "@shopify/polaris";
import { CancellationInterceptAccessStore } from "./store";
import { MediaUrlField } from "./MediaUrlField";
import { useCurrentOrganization } from "@vendor-app/utils/useCurrentOrganization";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

export function CancellationIntercept() {
  const { isCancellationInterceptEnabled } = useCurrentOrganization();

  const {
    initialize,
    toggleEnabled,
    setHeadline,
    setBodyMessage,
    setMediaUrl,
    setPrimaryButtonLabel,
    setSecondaryButtonLabel,
  } = CancellationInterceptAccessStore.useActions();

  const { headline, bodyMessage, mediaUrl, primaryButtonLabel, secondaryButtonLabel } =
    CancellationInterceptAccessStore.useFormFields();

  const isEnabled = CancellationInterceptAccessStore.useIsEnabled();
  const fieldErrors = CancellationInterceptAccessStore.useFieldErrors();

  const { addToast } = useToast();

  const initializeCancellationIntercept = async () => {
    const { success, errorMessage } = await initialize();
    if (!success && errorMessage) {
      addToast(errorMessage);
    }
  };

  useEffect(() => {
    void initializeCancellationIntercept();
    toggleEnabled(!!isCancellationInterceptEnabled);
  }, []);

  return (
    <LegacyCard sectioned>
      <LegacyStack vertical>
        {/* Heading */}
        <LegacyStack vertical spacing={"extraTight"}>
          <LegacyStack distribution={"equalSpacing"}>
            <Text variant="headingMd" as="h3">
              Cancellation intercept
            </Text>
            <FormControlLabel
              control={<Switch checked={isEnabled} inputProps={{ "aria-label": "controlled" }} color="success" />}
              value={isEnabled}
              onChange={(_, checked) => toggleEnabled(checked)}
              label={isEnabled ? "On" : "Off"}
              sx={{
                color: "gray",
                marginTop: "-7px",
              }}
            />
          </LegacyStack>
          <Text variant="bodySm" as="span" color="subdued">
            When a subscriber clicks cancel, this popup appears before the cancellation reasons. Use rich media to
            remind them of the value of their subscription.
          </Text>
        </LegacyStack>
        {isEnabled ? (
          <LegacyStack vertical>
            {/* Headline */}
            <LegacyStack vertical spacing={"extraTight"}>
              <Text variant="headingSm" as="h4">
                Headline
              </Text>
              <TextField
                autoComplete="off"
                label=""
                value={headline}
                onChange={v => setHeadline(v)}
                placeholder="Before you go..."
                showCharacterCount
                maxLength={60}
                error={fieldErrors.headline ? "Headline is required" : undefined}
              />
              <Text variant="bodySm" as="span" color="subdued">
                Shown as the title of the intercept popup
              </Text>
            </LegacyStack>
            {/* Body Message */}
            <LegacyStack vertical spacing={"extraTight"}>
              <Text variant="headingSm" as="h4">
                Body Message
              </Text>
              <TextField
                autoComplete="off"
                label=""
                value={bodyMessage}
                onChange={v => setBodyMessage(v)}
                placeholder="We'd hate to see you leave! Here's a quick look at what you'll be missing. Your subscription keeps you stocked with the products you love, delivered on your schedule."
                showCharacterCount
                maxLength={300}
                multiline
              />
              <Text variant="bodySm" as="span" color="subdued">
                Persuasive copy displayed below the headline
              </Text>
            </LegacyStack>
            {/* Media URL */}
            <MediaUrlField value={mediaUrl} onChange={setMediaUrl} />
            {/* Button labels */}
            <LegacyStack distribution="fillEvenly">
              <LegacyStack vertical spacing="extraTight">
                <Text variant="headingSm" as="h4">
                  Primary button label
                </Text>
                <TextField
                  autoComplete="off"
                  label=""
                  value={primaryButtonLabel}
                  onChange={v => setPrimaryButtonLabel(v)}
                  placeholder="Keep my subscription"
                  showCharacterCount
                  maxLength={23}
                  error={fieldErrors.primaryButtonLabel ? "Primary button label is required" : undefined}
                />
              </LegacyStack>

              <LegacyStack vertical spacing="extraTight">
                <Text variant="headingSm" as="h4">
                  Secondary button label
                </Text>
                <TextField
                  autoComplete="off"
                  label=""
                  value={secondaryButtonLabel}
                  onChange={v => setSecondaryButtonLabel(v)}
                  placeholder="Continue cancellation"
                  showCharacterCount
                  maxLength={25}
                  error={fieldErrors.secondaryButtonLabel ? "Secondary button label is required" : undefined}
                />
              </LegacyStack>
            </LegacyStack>
          </LegacyStack>
        ) : null}
      </LegacyStack>
    </LegacyCard>
  );
}
